import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, KeyRound, Mail, UserCheck, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

// User-friendly error messages mapping
const getReadableErrorMessage = (error: string, description: string | null): string => {
  const errorLower = error.toLowerCase();
  const descLower = (description || '').toLowerCase();

  // Link expired errors
  if (errorLower.includes('expired') || descLower.includes('expired') ||
      errorLower.includes('otp_expired') || descLower.includes('otp_expired')) {
    return 'This link has expired. Please request a new one.';
  }

  // Invalid or already used link
  if (errorLower.includes('invalid') || descLower.includes('invalid') ||
      errorLower.includes('otp_disabled') || descLower.includes('already')) {
    return 'This link is no longer valid. It may have already been used or expired.';
  }

  // Rate limit
  if (errorLower.includes('rate') || descLower.includes('rate') ||
      errorLower.includes('too_many')) {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }

  // Access denied
  if (errorLower.includes('access_denied') || errorLower.includes('unauthorized')) {
    return 'Access was denied. Please try signing in again.';
  }

  // Default to description or generic message
  return description || 'Something went wrong. Please try again.';
};

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');
  const [subMessage, setSubMessage] = useState<string | null>(null);
  const [authType, setAuthType] = useState<string | null>(null);
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get the hash fragment from the URL
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');
      const hashError = hashParams.get('error');
      const hashErrorDescription = hashParams.get('error_description');

      // Also check query params (some providers use query instead of hash)
      const queryParams = new URLSearchParams(location.search);
      const queryError = queryParams.get('error');
      const queryErrorDescription = queryParams.get('error_description');
      const queryErrorCode = queryParams.get('error_code');

      // Use whichever error is present
      const error = hashError || queryError;
      const errorDescription = hashErrorDescription || queryErrorDescription;

      setAuthType(type);

      if (error) {
        setStatus('error');
        const readableMessage = getReadableErrorMessage(error, errorDescription);
        setMessage(readableMessage);

        // Show specific sub-messages based on error type
        if (error.includes('expired') || errorDescription?.includes('expired')) {
          setSubMessage('Verification links expire after 24 hours for security.');
          setShowRetry(true);
        } else if (error.includes('invalid') || errorDescription?.includes('invalid')) {
          setSubMessage('Each link can only be used once.');
          setShowRetry(true);
        }

        // Don't auto-redirect for errors - let user take action
        return;
      }

      if (accessToken && refreshToken) {
        setStatus('success');

        // Check for pending invitation redirect
        const pendingReturn = localStorage.getItem('pendingInvitationReturn');
        const redirectTo = pendingReturn || '/dashboard';
        if (pendingReturn) {
          localStorage.removeItem('pendingInvitationReturn');
        }

        // Handle different auth types
        switch (type) {
          case 'signup':
            // Email verification after signup - auto login and redirect quickly
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('refresh_token', refreshToken);
            setMessage(pendingReturn ? 'Account verified!' : 'Welcome to TinyScale!');
            setSubMessage(pendingReturn
              ? 'Taking you to accept your invitation...'
              : 'Your email is verified. Taking you to your dashboard...');
            // Quick redirect - user is eager to get started
            setTimeout(() => {
              window.location.href = redirectTo;
            }, 800);
            break;

          case 'recovery':
            // Password reset - redirect to reset password page with token
            setMessage('Verified!');
            setSubMessage('Redirecting to set your new password...');
            setTimeout(() => {
              navigate('/reset-password', {
                state: { accessToken },
                replace: true
              });
            }, 800);
            break;

          case 'magiclink':
            // Magic link login - auto login
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('refresh_token', refreshToken);
            setMessage('Signed in successfully!');
            setSubMessage(pendingReturn
              ? 'Taking you to accept your invitation...'
              : 'Taking you to your dashboard...');
            setTimeout(() => {
              window.location.href = redirectTo;
            }, 800);
            break;

          case 'invite':
            // Organization invitation - auto login
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('refresh_token', refreshToken);
            setMessage('Invitation accepted!');
            setSubMessage('Taking you to your dashboard...');
            setTimeout(() => {
              window.location.href = redirectTo;
            }, 800);
            break;

          default:
            // Default: treat as login - auto login
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('refresh_token', refreshToken);
            setMessage('Signed in!');
            setSubMessage(pendingReturn
              ? 'Taking you to accept your invitation...'
              : 'Taking you to your dashboard...');
            setTimeout(() => {
              window.location.href = redirectTo;
            }, 800);
        }
      } else {
        // No tokens found
        setStatus('error');
        setMessage('Unable to complete verification');
        setSubMessage('The link may be incomplete or malformed. Please request a new one.');
        setShowRetry(true);
      }
    } catch (err) {
      console.error('Auth callback error:', err);
      setStatus('error');
      setMessage('Something went wrong');
      setSubMessage('Please try again or contact support if the problem persists.');
      setShowRetry(true);
    }
  };

  const handleRetry = () => {
    // Redirect based on what they were trying to do
    if (authType === 'recovery') {
      navigate('/login', { state: { showForgotPassword: true } });
    } else if (authType === 'magiclink') {
      navigate('/login', { state: { showMagicLink: true } });
    } else {
      navigate('/login');
    }
  };

  const getIcon = () => {
    if (status === 'loading') {
      return <Loader2 className="h-8 w-8 animate-spin text-primary" />;
    }
    if (status === 'error') {
      return <XCircle className="h-8 w-8 text-destructive" />;
    }
    // Success icons based on auth type
    switch (authType) {
      case 'recovery':
        return <KeyRound className="h-8 w-8 text-green-500" />;
      case 'magiclink':
        return <Mail className="h-8 w-8 text-green-500" />;
      case 'signup':
        return <UserCheck className="h-8 w-8 text-green-500" />;
      default:
        return <CheckCircle className="h-8 w-8 text-green-500" />;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 pb-6">
          <div className="flex flex-col items-center space-y-4">
            {getIcon()}

            <div className="text-center space-y-2">
              <p className={`text-lg font-medium ${status === 'error' ? 'text-destructive' : status === 'loading' ? 'text-muted-foreground' : 'text-foreground'}`}>
                {message}
              </p>

              {subMessage && (
                <p className="text-sm text-muted-foreground">
                  {subMessage}
                </p>
              )}
            </div>

            {status === 'error' && showRetry && (
              <div className="flex flex-col gap-2 w-full pt-4">
                <Button
                  onClick={handleRetry}
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {authType === 'recovery' ? 'Request new reset link' :
                   authType === 'magiclink' ? 'Request new magic link' :
                   'Back to login'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="w-full text-muted-foreground"
                >
                  Go to login
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};